import React, { useEffect, useState, useMemo } from 'react';
import { Drawer, Spin, Alert, Typography, Space, Tag, Button, Divider, Empty } from 'antd';
import { FileTextOutlined, ToolOutlined, UserOutlined, AlertOutlined } from '@ant-design/icons';
import HallazgoChip from 'components/admin/Hallazgos/HallazgoChip';
import MonedaBadge from 'components/admin/Hallazgos/MonedaBadge';
import { getBudgetSummaryApi } from 'api/hallazgoFlujo';
import { useTrazabilidadDrawer } from 'providers/Trazabilidad/TrazabilidadDrawerProvider';
import { useTenantColors } from 'hooks/useTenantColors';
import DownloadBudgetButton from './downloads/DownloadBudgetButton';
import {
  drawerPropsFromLevel,
  fmtDateTime,
  fmtMoney,
  STATUS_LABEL,
  STATUS_ANTD_COLOR,
} from './drawerShared';

// Fase 9 del roadmap: drawer de summary de presupuesto.
// Muestra cliente, estado, items, hallazgos vinculados (clickeables para
// anidar), ODT asociada si existe, subtotal con conversion si es UF/USD.

function BudgetSummaryDrawer({ open, idBudget, onClose, level = 0 }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { openHallazgo, openOrder } = useTrazabilidadDrawer();
  const { secondary } = useTenantColors();

  useEffect(() => {
    if (!open || !idBudget) return;
    setLoading(true);
    setError(null);
    setData(null);
    getBudgetSummaryApi(idBudget)
      .then((res) => {
        if (res && res.id) setData(res);
        else setError(res?.message || 'No se pudo cargar el presupuesto');
      })
      .catch((err) => setError(err?.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [open, idBudget]);

  const subtotal = useMemo(() => {
    if (!data?.items) return 0;
    return data.items.reduce(
      (acc, it) => acc + Number(it.unitValue || 0) * Number(it.quantity || 0),
      0
    );
  }, [data]);

  return (
    <Drawer
      {...drawerPropsFromLevel(level)}
      open={open}
      onClose={onClose}
      width={620}
      destroyOnHidden
      title={
        <Space align="center" size="middle">
          <FileTextOutlined style={{ color: secondary }} />
          <span>Presupuesto {idBudget ? `#${idBudget}` : ''}</span>
          {data?.status ? (
            <Tag color={STATUS_ANTD_COLOR[data.status] ?? 'default'}>
              {STATUS_LABEL[data.status] ?? data.status}
            </Tag>
          ) : null}
          {data?.id ? <DownloadBudgetButton idBudget={data.id} size="small" /> : null}
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : data ? (
        <>
          {/* Cliente + metadata */}
          {data.cliente ? (
            <div style={{ marginBottom: 16 }}>
              <Space align="center">
                <UserOutlined style={{ color: '#64748b' }} />
                <Typography.Text strong>{data.cliente.name}</Typography.Text>
              </Space>
            </div>
          ) : null}
          {data.reason ? (
            <Typography.Paragraph style={{ marginBottom: 4 }}>
              <strong>{data.reason}</strong>
            </Typography.Paragraph>
          ) : null}
          {data.description ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
              {data.description}
            </Typography.Paragraph>
          ) : null}
          {data.finishTime ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
              <strong>Tiempo de producción:</strong> {data.finishTime}
            </Typography.Paragraph>
          ) : null}

          {/* Hallazgos cubiertos */}
          {data.hallazgos && data.hallazgos.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <Typography.Text
                strong
                style={{
                  fontSize: 11,
                  letterSpacing: 0.8,
                  color: '#64748b',
                  display: 'block',
                  marginBottom: 8,
                }}
              >
                HALLAZGOS CUBIERTOS · {data.hallazgos.length}
              </Typography.Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.hallazgos.map((h) => (
                  <HallazgoChip
                    key={h.id}
                    hallazgo={h}
                    compact
                    onClick={() => openHallazgo(h.id)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {/* ODT asociada */}
          {data.order ? (
            <div style={{ marginBottom: 16 }}>
              <Button
                block
                icon={<ToolOutlined />}
                onClick={() => openOrder(data.order.id)}
                style={{ textAlign: 'left' }}
              >
                ODT #{data.order.id}
                {data.order.completed_at
                  ? ` · finalizada ${fmtDateTime(data.order.completed_at)}`
                  : ' · en curso'}
              </Button>
            </div>
          ) : null}

          <Divider style={{ margin: '12px 0' }} />

          {/* Items */}
          <Typography.Text
            strong
            style={{
              fontSize: 11,
              letterSpacing: 0.8,
              color: '#64748b',
              display: 'block',
              marginBottom: 8,
            }}
          >
            ITEMS · {data.items?.length ?? 0}
          </Typography.Text>
          {data.items && data.items.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.items.map((it) => (
                <div
                  key={it.id}
                  style={{
                    padding: 8,
                    background: '#f6f7f9',
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 13,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#1e293b',
                      }}
                    >
                      {it.description || '—'}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {it.elevator || '—'} · cant {it.quantity ?? 0}
                      {it.id_coleccion_item ? ' · 🔗 catálogo' : ''}
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      color: '#1e293b',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtMoney(Number(it.unitValue) * Number(it.quantity), data.moneda)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty description="Sin items" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          {/* Subtotal */}
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: '#0f172a',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text
              style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, letterSpacing: 0.8 }}
            >
              SUBTOTAL
            </Typography.Text>
            <MonedaBadge amount={subtotal} moneda={data.moneda || 'CLP'} size="lg" theme="dark" />
          </div>

          {/* Rechazo si aplica */}
          {data.status === 'rechazado' && data.razon_rechazo ? (
            <Alert
              type="warning"
              showIcon
              icon={<AlertOutlined />}
              style={{ marginTop: 16 }}
              message={`Rechazado: ${data.razon_rechazo_categoria || 'sin categoría'}`}
              description={data.razon_rechazo}
            />
          ) : null}
        </>
      ) : null}
    </Drawer>
  );
}

export default BudgetSummaryDrawer;
